import React, { useState, useEffect, useCallback, useRef } from "react";
import './Modal.styles.scss';

const Modal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  content, 
  requireNote = false,
  noteLabel = "Note",
  notePlaceholder = "Enter your note here...",
  initialNote = "",
  // New props for custom inputs
  customInputs = [], // Array of input configurations
  // Legacy props for backward compatibility
  showNoteInput = true
}) => {
  const [inputs, setInputs] = useState({});
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  
  // Use ref to track if we've initialized inputs for the current modal session
  const initializedRef = useRef(false);
  // Use ref to store stable reference to customInputs
  const customInputsRef = useRef(customInputs);

  // Initialize inputs when modal opens
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      const initialInputs = {};
      
      // Add note input if showNoteInput is true (backward compatibility)
      if (showNoteInput) {
        initialInputs.note = initialNote;
      }
      
      // Add custom inputs
      customInputs.forEach(input => {
        initialInputs[input.key] = input.initialValue || "";
      });
      
      setInputs(initialInputs);
      setErrors({});
      initializedRef.current = true;
      customInputsRef.current = customInputs;
    } else if (!isOpen) {
      // Reset when modal closes
      setInputs({});
      setErrors({});
      initializedRef.current = false;
    }
  }, [isOpen, initialNote, showNoteInput]); // Removed customInputs from dependencies

  // Only update inputs if there are actual changes to customInputs structure
  useEffect(() => {
    if (!isOpen || !initializedRef.current) return;

    // Check if customInputs structure has changed
    const hasStructuralChanges = customInputs.length !== customInputsRef.current.length ||
      customInputs.some((input, index) => {
        const prevInput = customInputsRef.current[index];
        return !prevInput || input.key !== prevInput.key;
      });

    if (hasStructuralChanges) {
      setInputs(prevInputs => {
        const newInputs = { ...prevInputs };
        
        // Add any new custom inputs that don't exist
        customInputs.forEach(input => {
          if (!(input.key in prevInputs)) {
            newInputs[input.key] = input.initialValue || "";
          }
        });
        
        return newInputs;
      });
      customInputsRef.current = customInputs;
    }
  }, [customInputs, isOpen]);

  // Validate inputs
  useEffect(() => {
    if (!isOpen) return;
    
    const newErrors = {};
    let valid = true;

    // Validate note input (if shown)
    if (showNoteInput) {
      const note = inputs.note || "";
      if (requireNote) {
        if (note.length === 0) {
          newErrors.note = "Note is required";
          valid = false;
        } else if (note.length < 4) {
          newErrors.note = "Note must be at least 4 characters long";
          valid = false;
        }
      } else if (note.length > 0 && note.length < 4) {
        newErrors.note = "Note must be at least 4 characters long";
        valid = false;
      }
    }

    // Validate custom inputs
    customInputsRef.current.forEach(input => {
      const value = inputs[input.key] || "";
      
      if (input.required && value.trim().length === 0) {
        newErrors[input.key] = `${input.label} is required`;
        valid = false;
      } else if (input.minLength && value.length > 0 && value.length < input.minLength) {
        newErrors[input.key] = `${input.label} must be at least ${input.minLength} characters long`;
        valid = false;
      } else if (input.pattern && value.length > 0 && !input.pattern.test(value)) {
        newErrors[input.key] = input.patternError || `${input.label} format is invalid`;
        valid = false;
      }
    });

    setErrors(newErrors);
    setIsValid(valid);
  }, [inputs, requireNote, showNoteInput, isOpen]);

  const handleInputChange = useCallback((key, value) => {
    setInputs(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!isValid) return;
    
    // For backward compatibility, if only note input exists, pass just the note
    if (showNoteInput && customInputsRef.current.length === 0) {
      onConfirm(inputs.note);
    } else {
      // Pass all inputs as an object
      onConfirm(inputs);
    }
  }, [isValid, showNoteInput, inputs, onConfirm]);

  const handleClose = useCallback(() => {
    setInputs({});
    setErrors({});
    initializedRef.current = false;
    onClose();
  }, [onClose]);

  const getInputClassName = useCallback((key) => {
    const value = inputs[key] || "";
    let className = "note-input";
    if (value.length > 0) {
      className += errors[key] ? " error" : " valid";
    }
    return className;
  }, [inputs, errors]);

  if (!isOpen) return null;

  return (
    <div className={`modal ${isOpen ? "open" : ""}`}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>
        <div className="modal-body">
          {content && (
            <div className="content">
              {content}
            </div>
          )}
          
          {/* Note input (backward compatibility) */}
          {showNoteInput && (
            <div className="note-input-container">
              <label htmlFor="note-input">
                {noteLabel}{requireNote && " *"}
              </label>
              <textarea
                id="note-input"
                className={getInputClassName('note')}
                value={inputs.note || ""}
                onChange={(e) => handleInputChange('note', e.target.value)}
                placeholder={notePlaceholder}
                rows={3}
              />
              {errors.note && (
                <div className="error-message">
                  {errors.note}
                </div>
              )}
            </div>
          )}

          {/* Custom inputs */}
          {customInputsRef.current.map((input) => (
            <div key={input.key} className="note-input-container">
              <label htmlFor={`${input.key}-input`}>
                {input.label}{input.required && " *"}
              </label>
              {input.type === 'textarea' ? (
                <textarea
                  id={`${input.key}-input`}
                  className={getInputClassName(input.key)}
                  value={inputs[input.key] || ""}
                  onChange={(e) => handleInputChange(input.key, e.target.value)}
                  placeholder={input.placeholder}
                  rows={input.rows || 3}
                />
              ) : (
                <input
                  id={`${input.key}-input`}
                  type={input.type || 'text'}
                  className={getInputClassName(input.key)}
                  value={inputs[input.key] || ""}
                  onChange={(e) => handleInputChange(input.key, e.target.value)}
                  placeholder={input.placeholder}
                />
              )}
              {errors[input.key] && (
                <div className="error-message">
                  {errors[input.key]}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="modal-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button 
            className="modal-confirm" 
            onClick={handleConfirm}
            disabled={!isValid}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Modal);